import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import Layout from "@/components/Layout";
import SectionHeader from "@/components/SectionHeader";
import { useI18n } from "@/i18n";
import { services, serviceCategories } from "@/data/services";

export default function Services() {
  const { t, lang } = useI18n();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");

  const filtered = useMemo(() => {
    return services.filter((s) => {
      const title = (lang === "lt" ? s.title.lt : s.title.en).toLowerCase();
      const matchQ = q ? title.includes(q.toLowerCase()) : true;
      const matchC = cat === "all" ? true : s.category === cat;
      return matchQ && matchC;
    });
  }, [q, cat, lang]);

  return (
    <Layout seoTitle={t("services.title")} seoDescription={t("services.subtitle")}>
      <section className="section-y">
        <div className="container-mt">
          <SectionHeader as="h1" num="01" kicker="Services" title={t("services.title")} subtitle={t("services.subtitle")} />

          <div className="flex flex-col md:flex-row gap-4 mb-10 md:mb-12">
            <div className="relative flex-1 max-w-md">
              <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="services-search"
                aria-label={lang === "lt" ? "Ieškoti paslaugos" : "Search service"}
                placeholder={lang === "lt" ? "Ieškoti paslaugos..." : "Search service..."}
                className="input-mt pl-11"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setCat("all")}
                data-testid="cat-all"
                aria-pressed={cat === "all"}
                className={`px-4 py-2 rounded-full text-[11px] font-mono uppercase tracking-[0.22em] border transition-colors duration-200 ${
                  cat === "all"
                    ? "border-brand-lime text-brand-lime bg-brand-lime/10"
                    : "border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600"
                }`}
              >
                {lang === "lt" ? "Visos" : "All"}
              </button>
              {serviceCategories.map((c) => (
                <button
                  key={c.key}
                  onClick={() => setCat(c.key)}
                  data-testid={`cat-${c.key}`}
                  aria-pressed={cat === c.key}
                  className={`px-4 py-2 rounded-full text-[11px] font-mono uppercase tracking-[0.22em] border transition-colors duration-200 ${
                    cat === c.key
                      ? "border-brand-lime text-brand-lime bg-brand-lime/10"
                      : "border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600"
                  }`}
                >
                  {t(c.labelKey)}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {filtered.map((s, i) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="card-mt card-mt-pad card-interactive group flex flex-col"
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="num-chip">{String(i + 1).padStart(2, "0")}</span>
                  <span className="eyebrow text-[10px]">{t(`services.category${s.category.charAt(0).toUpperCase() + s.category.slice(1)}`)}</span>
                </div>
                <h2 className="h-display text-2xl md:text-[1.65rem] text-white mb-3 leading-tight">
                  {lang === "lt" ? s.title.lt : s.title.en}
                </h2>
                <p className="text-zinc-400 text-sm leading-relaxed mb-6 flex-1">
                  {lang === "lt" ? s.short.lt : s.short.en}
                </p>
                <span className="inline-flex items-center gap-2 text-brand-lime font-mono uppercase text-[10px] tracking-[0.22em] mt-auto">
                  {t("common.readMore")}
                  <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-24">
              <p className="text-zinc-500 font-mono uppercase tracking-widest text-xs">— No matching services —</p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
